"use client"

import { Fragment } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"
import { StepPhotoIcon, StepLocationIcon, StepPlanIcon } from "./section-icons"

export type WizardStep = 1 | 2 | 3

/** Which flow the indicator is labelling. Step 3 delivers a different artifact
 *  in each one, so the last label is not shared. */
export type WizardMode = "identify" | "diagnose"

interface WizardProgressProps {
  currentStep: WizardStep
  mode?: WizardMode
  className?: string
}

type WizardStepDefinition = {
  id: WizardStep
  label: string
  shortLabel: string
  Icon: (props: { className?: string }) => React.ReactNode
}

const FINAL_STEP: Record<WizardMode, { label: string; shortLabel: string }> = {
  identify: { label: "Get your result", shortLabel: "Result" },
  diagnose: { label: "Get your diagnosis", shortLabel: "Diagnosis" },
}

function buildSteps(mode: WizardMode): WizardStepDefinition[] {
  return [
    { id: 1, label: "Add a photo", shortLabel: "Photo", Icon: StepPhotoIcon },
    { id: 2, label: "Set your ZIP", shortLabel: "ZIP", Icon: StepLocationIcon },
    { id: 3, ...FINAL_STEP[mode], Icon: StepPlanIcon },
  ]
}

export function WizardProgress({ currentStep, mode = "identify", className }: WizardProgressProps) {
  const reduce = useReducedMotion()
  const steps = buildSteps(mode)

  return (
    <ol
      aria-label="Progress"
      className={cn("flex items-start justify-center mb-6", className)}
    >
      {steps.map((step, index) => {
        const isComplete = step.id < currentStep
        const isCurrent = step.id === currentStep
        const isPending = !isCurrent && !isComplete

        return (
          <Fragment key={step.id}>
            <li
              aria-current={isCurrent ? "step" : undefined}
              className="flex flex-col items-center gap-1 flex-1 max-w-[140px]"
            >
              <motion.div
                aria-hidden="true"
                // Key on completion state so motion remounts the bubble exactly
                // when the step advances, replaying the scale pop without firing
                // on every render.
                key={isComplete ? "complete" : "active"}
                initial={reduce || (!isComplete && currentStep === 1) ? false : { scale: 0.78 }}
                animate={{ scale: 1 }}
                transition={
                  isComplete
                    ? { type: "spring", stiffness: 360, damping: 18 }
                    : { duration: 0.18, ease: [0.22, 1, 0.36, 1] }
                }
                className={cn(
                  "relative flex items-center justify-center w-9 h-9 rounded-full transition-colors",
                  isCurrent && "bg-green-100 text-green-700",
                  isComplete && "bg-green-600 text-white",
                  isPending && "bg-gray-100 text-gray-400",
                )}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isComplete ? (
                    <motion.span
                      key="check"
                      initial={reduce ? false : { scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={reduce ? undefined : { scale: 0.4, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 420, damping: 20 }}
                      className="inline-flex"
                    >
                      <Check className="w-4 h-4" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="icon"
                      initial={reduce ? false : { scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={reduce ? undefined : { scale: 0.9, opacity: 0 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      className="inline-flex"
                    >
                      <step.Icon className="w-5 h-5" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
              <span
                className={cn(
                  "text-[10px] sm:text-[11px] font-medium leading-tight text-center transition-colors duration-200",
                  isCurrent && "text-gray-900",
                  isComplete && "text-green-700",
                  isPending && "text-gray-400",
                )}
              >
                <span className="hidden sm:inline">{step.label}</span>
                <span className="sm:hidden">{step.shortLabel}</span>
                {/* Colour and the check glyph are the only visual carriers of
                    completed and upcoming, so name those for assistive tech.
                    The current step is left to aria-current on the <li>, which
                    already announces it. */}
                {!isCurrent && (
                  <span className="sr-only">{isComplete ? " (completed)" : " (upcoming)"}</span>
                )}
              </span>
            </li>
            {index < steps.length - 1 && (
              <li
                aria-hidden="true"
                className="flex items-center h-9 mx-1 sm:mx-2 self-start"
              >
                <span
                  className={cn(
                    "block w-4 sm:w-8 border-t border-dashed transition-colors duration-300",
                    step.id < currentStep ? "border-green-400" : "border-gray-300",
                  )}
                />
              </li>
            )}
          </Fragment>
        )
      })}
    </ol>
  )
}
